import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Text, Toggle } from '@intellecteu/common-ui';
import { useNotifications } from 'app/utils';
import { validatorsPath } from 'app/constants';
import { QueryKeys, useDeleteValidator } from 'data';
import { Modal } from '../modal';

import s from './styles.module.scss';

interface ValidatorDeleteModalProps {
  validatorName: string;
  onClose: () => void;
  onSettled?: () => void;
  customAuth?: boolean;
}

export const ValidatorDeleteModal = ({ validatorName, onClose, onSettled, customAuth }: ValidatorDeleteModalProps) => {
  const [deleteValidatorWithResources, setDeleteValidatorWithResources] = useState(true);
  const { mutate: deleteValidator, isLoading: isValidatorDeleting } = useDeleteValidator(deleteValidatorWithResources);
  const queryClient = useQueryClient();
  const { onSuccessValidatorRemove } = useNotifications();
  const navigate = useNavigate();
  let timeoutId: null | ReturnType<typeof setTimeout> = null;

  const onSuccess = async () => {
    onSuccessValidatorRemove(validatorName);
    navigate(validatorsPath);
    await queryClient.cancelQueries({ queryKey: [QueryKeys.VALIDATORS], type: 'all' });

    timeoutId = setTimeout(() => {
      queryClient.invalidateQueries([QueryKeys.VALIDATORS]);
    }, 0);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Modal
      withConfirm
      onSubmit={() => {
        deleteValidator(validatorName, {
          onSuccess,
          onSettled,
        });
      }}
      onClose={onClose}
      title="Remove validator"
      text={`Are you sure you want to remove ${validatorName} ?`}
      isLoading={isValidatorDeleting}
      submitButtonProps={{ type: 'button' }}
      submitText="Confirm"
    >
      <div className={s.copyModalContent}>
        <div className={s.restore}>
          <div className={s.copyModalContentHeader}>
            <div className={s.restoreInfo}>
              {!customAuth && (
                <Text color={Text.color.accentOrange}>Delete Keycloak configuration for this validator?</Text>
              )}

              <Text color={Text.color.accentOrange}>This will break restoring from identity backups.</Text>
            </div>

            <Toggle.Small
              toggleClassName={s.toggle}
              label="Delete all validator's resources"
              checked={!!deleteValidatorWithResources}
              onChange={() => setDeleteValidatorWithResources(!deleteValidatorWithResources)}
              showInnerLabel={false}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
